import { createGlobalStyle } from 'styled-components'
import { themeGet } from 'styled-system'

/**
 * Page-wide print rules for the Individual Resource pages.
 *
 * These intentionally target elements across the whole page (not just this
 * component) via stable hooks: the `data-print-hidden` attributes the pages
 * add to their controls, the `.individual-resource-print-page` container
 * class, and a few global widget ids/classes. We avoid matching
 * styled-components class names directly, since those readable names are
 * stripped in production builds and would silently stop matching.
 */
export const PrintStyles = createGlobalStyle`
  @media print {
    /* Hide non-print controls on this page */
    [data-print-hidden='navigation'],
    [data-print-hidden='mark-done'],
    [data-print-hidden='teach-button'],
    [data-print-hidden='language-toggle'],
    [data-print-hidden='print-button'],
    [data-print-hidden='program-nav'],
    [data-print-hidden='references'],
    ._pendo-step-container-styles,
    [class*='_pendo'],
    #help-button,
    .global-nav-container,
    [data-testid='global-footer-container'] {
      display: none !important;
    }

    /* Controls are meaningless on paper — hide buttons, toggles, and inputs */
    .individual-resource-print-page button,
    .individual-resource-print-page [role='button'],
    .individual-resource-print-page input,
    .individual-resource-print-page select,
    .individual-resource-print-page textarea {
      display: none !important;
    }

    /* Keep link text on paper, but strip the clickable affordance so it
       reads as plain text */
    .individual-resource-print-page a[href] {
      pointer-events: none;
      cursor: default;
      text-decoration: none;
      color: inherit !important;
    }

    /* Don't let a page break slice through a text block. Without this, a break
       that lands mid-paragraph (e.g. a references entry) cuts a line in half —
       which is the clipping seen on the last page. Move the whole block to the
       next page instead. */
    .individual-resource-print-page p,
    .individual-resource-print-page li,
    .individual-resource-print-page h1,
    .individual-resource-print-page h2,
    .individual-resource-print-page h3,
    .individual-resource-print-page h4 {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    /* Remove the default bottom page margin and container padding so the fixed
       copyright line can sit flush against the bottom edge and content can use
       the full page height. */
    @page {
      margin-bottom: 0in;
    }

    .individual-resource-print-page {
      padding-bottom: 0rem;
    }

    /* Repeat the copyright line at the bottom of every printed page. The opaque
       background masks any content that would otherwise show through behind the
       fixed line. */
    [data-print-copyright] {
      display: block !important;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 0rem 0.5rem 0rem;
      background: ${themeGet('colors.white')};
    }

    /* Shrink the printed output to fit fewer sheets. The layout is sized in
       rem, so lowering the root font size scales fonts and spacing together
       via a real reflow — unlike zoom/transform, which sliced content across
       page breaks. Tune this value to trade off page count vs. readability. */
    html {
      font-size: 60% !important;
    }

    /* Grommet renders links and a lot of body/markdown text with px font sizes
       (often as a bare text node inside a wrapper div), so the root font-size
       shrink above doesn't reach them and per-tag/testid hooks are unreliable.
       Force every non-heading element to a rem-based size so it scales with the
       html knob. Headings are excluded and keep their own rem sizes (which also
       scale); the page title is an <h1>, so it stays large. */
    .individual-resource-print-page
      *:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
      font-size: 1rem !important;
      line-height: 1.35 !important;
    }

    /* The "text link with icon" SVGs are sized via width/height attributes, so
       they don't shrink with the font-size scaling and end up oversized next to
       the shrunken text. Size them in rem (with viewBox preserving aspect) so
       they track the scaled text. */
    .individual-resource-print-page [data-testid^='text-link-item'] svg {
      width: auto !important;
      height: 1.25rem !important;
    }

    /* Drop heading padding-bottom to tighten vertical spacing in print. */
    .individual-resource-print-page h1,
    .individual-resource-print-page h2,
    .individual-resource-print-page h3,
    .individual-resource-print-page h4,
    .individual-resource-print-page h5,
    .individual-resource-print-page h6 {
      padding-bottom: 0 !important;
    }

    /* Drop the dl's leading space so it sits tight under its heading. */
    .individual-resource-print-page dl {
      padding-top: 0 !important;
      margin-top: 0 !important;
    }
  }
`

/**
 * High-school-only print fixes. The HS layout differs from K-8/MS: it uses
 * fixed 55rem content widths whose responsive shrink rules are `@media screen`
 * (so they don't apply in print and overflow the page), and a header that
 * pairs the title with a fixed-width decorative image in a flex row.
 */
export const HighSchoolPrintStyles = createGlobalStyle`
  @media print {
    /* Cap content to the printable width so headings/cards stop bleeding off
       the left and right page edges */
    .individual-resource-print-page .content,
    .individual-resource-print-page .content * {
      max-width: 100% !important;
    }

    /* Stack header content and drop the decorative image so the title gets the
       full width instead of collapsing to one letter per line */
    .individual-resource-print-page .content {
      display: block !important;
    }

    .individual-resource-print-page .resource-image {
      display: none !important;
    }
  }
`
